import ExpandableText from '@/components/ExpandableText';
import { Icons } from '@/components/icons';
import PriceDisplay from '@/components/PriceDisplay';
import { Button } from '@/components/primitives/Button';
import Divider from '@/components/primitives/Divider';
import { Typography } from '@/components/primitives/Typography';
import PromoCode from '@/components/PromoCode';
import Changeability from '@/components/purchase/card/Changeability';
import Refundability from '@/components/purchase/card/Refundability';
import { TransText } from '@/i18n/trans/text';
import { type AppliedReductionDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import type { FC } from 'react';

interface BaseOfferCardProps {
  title: string;
  amount: number;
  isSelected: boolean;
  onSelect: () => void;
  description?: string;
  isRefundable?: boolean;
  isChangeable?: boolean;
  showPassengerInfo?: boolean;
  appliedReduction?: AppliedReductionDTO;
  testId: string;
}

const BaseOfferCard: FC<BaseOfferCardProps> = ({
  title,
  amount,
  isSelected,
  onSelect,
  description,
  isRefundable = false,
  isChangeable = false,
  showPassengerInfo = false,
  appliedReduction,
  testId,
}) => {
  return (
    <div
      className={cn(
        'flex w-full flex-col justify-between overflow-hidden rounded-lg border border-primary hover:cursor-pointer hover:bg-primary-pale',
        { 'border-2 border-primary-dark bg-primary-light': isSelected }
      )}
      onClick={onSelect}
      data-testid="offer-item"
    >
      <div className="flex flex-grow flex-col gap-1 px-4 py-3 laptop:gap-0">
        <div className="flex flex-row items-center justify-between">
          <Typography variant="body1-bold">
            <span>{title}</span>
          </Typography>
          <Typography variant="body1-bold" className="text-primary">
            <PriceDisplay amount={amount} />
          </Typography>
        </div>
        {appliedReduction?.code && (
          <PromoCode
            className="mt-2"
            code={appliedReduction.code}
            description={appliedReduction.description}
          />
        )}
        <Divider className="my-3" variant={isSelected ? 'medium' : 'regular'} />
        <ExpandableText text={description} />
        <div className="flex flex-col">
          <div className="flex flex-row gap-1">
            <Refundability condition={isRefundable} />
            <Changeability condition={isChangeable} />
          </div>
          {showPassengerInfo && (
            <div className="mt-2 flex flex-row gap-1">
              <Icons.users className="text-primary" />
              <Typography variant="body2">
                <TransText i18nKey="priceIsForAllPassengers" />
              </Typography>
            </div>
          )}
        </div>
      </div>
      <Button
        variant={isSelected ? 'primary' : 'secondary'}
        data-testid={testId}
        className={cn('mt-1 w-full rounded-none border-0', {
          'border-t': !isSelected,
        })}
      >
        {isSelected && (
          <Icons.checkCircle width={20} height={20} className="self-center" />
        )}
        <Typography variant="body1-bold">
          <TransText i18nKey={isSelected ? 'selected' : 'select'} />
        </Typography>
      </Button>
    </div>
  );
};

export default BaseOfferCard;
